import { relativeTimestampOperatorOf, timestampVariantOf } from './operators';

/**
 * The relative-timestamp arithmetic behind the date editors: a stored offset in
 * seconds, shown and edited as a whole day count plus a direction.
 */

export const TIME_DIRECTION_AGO = 'ago';
export const TIME_DIRECTION_FROM_NOW = 'from now';
export const SECONDS_IN_DAY = 86_400;

/** A fresh relative-date row starts where the legacy editor did. */
export const ONE_DAY_FROM_NOW_SECONDS = String(SECONDS_IN_DAY);

export function isRelativeTimestampOperator(operator: string): boolean {
  return timestampVariantOf(operator) === 'relative';
}

export function isAbsoluteTimestampOperator(operator: string): boolean {
  return timestampVariantOf(operator) === 'absolute';
}

export function isTimestampInequalityOperator(operator: string): boolean {
  return timestampVariantOf(operator) != null;
}

/**
 * The dropdown lists only the relative spelling of each paired operator, so an
 * absolute one selects its relative twin — the variant select carries the rest.
 */
export function normalizeTimestampOperatorForSelect(operator: string): string {
  return relativeTimestampOperatorOf(operator);
}

// A relative offset is stored in seconds and shown in whole days. The day count
// is rounded for display only: writing back happens through the setters below,
// so a reader who doesn't touch the row can't turn 3600 into 86400.

export function relativeTimestampDays(value: string | null): number {
  return Math.abs(Math.round(Number(value ?? '0') / SECONDS_IN_DAY));
}

export function relativeTimestampDirection(value: string | null): string {
  return Number(value ?? '0') < 0 ? TIME_DIRECTION_AGO : TIME_DIRECTION_FROM_NOW;
}

/**
 * Value for a day count the reader typed, keeping the current direction. Typing
 * the count the field already shows leaves the value alone: the display is
 * rounded, so echoing it back would flatten a sub-day offset — clearing the
 * field on a row showing 0 for -3600 would otherwise commit 0. Reaching 0
 * deliberately still works, via any other count and back.
 *
 * A typed count is rounded so the field always stores what it displays; only a
 * value that arrived on the wire can be finer than a day.
 */
export function relativeTimestampForTypedDays(typed: string, value: string | null): string {
  const days = Math.round(Number(typed));

  if (value != null && days === relativeTimestampDays(value)) {
    return value;
  }
  return String(days * SECONDS_IN_DAY * (relativeTimestampDirection(value) === TIME_DIRECTION_AGO ? -1 : 1));
}

/** Seconds for a direction the reader picked, keeping the current magnitude. */
export function relativeTimestampWithDirection(value: string | null, direction: string): string {
  return String(Math.abs(Number(value ?? '0')) * (direction === TIME_DIRECTION_AGO ? -1 : 1));
}
